import { useToast } from 'modules/toast/hooks/useToast';
import { cvReuploadExtractionFailed, extractionError } from 'modules/toast/toastDictionary';
import { useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { RealtimeAudienceEnum } from 'realtimer/realtimerAudiences';
import { useExpertHubStateLazyQuery } from 'shared/hooks/api/expertHubState/expertHubState.generated';
import { useExpertLastExtractionLazyQuery } from 'shared/hooks/api/extractionTrigger/extractionTrigger.generated';
import { useAuthStore } from 'shared/hooks/stores/useAuthStore';
import { useExpertProfileStore } from 'shared/hooks/stores/useExpertProfileStore';
import { useHubStateStore } from 'shared/hooks/stores/useHubStateStore';
import { useRealtimer } from 'shared/hooks/useRealtimer';
import { ExtractionStateEnum } from 'types/enums/extraction-state.enum';
import { useShallow } from 'zustand/react/shallow';

export const useExtractionNotifications = () => {
  const { t } = useTranslation();
  const { displayToast, setExtractionAcknowledged } = useToast();

  const expertId = useAuthStore((state) => state.user?.expertId)!;
  const isExtractionAcknowledged = useExpertProfileStore(
    (state) => state.expertProfile?.isExtractionAcknowledged ?? false,
  )!;
  const { extractionState, loadHubStateData } = useHubStateStore(
    useShallow((state) => ({
      extractionState: state.extractionState,
      finishedAt: state.finishedAt,
      loadHubStateData: state.loadHubStateData,
    })),
  );

  const [getExpertHubState] = useExpertHubStateLazyQuery({
    fetchPolicy: 'no-cache',
  });
  const [getExpertLastExtraction] = useExpertLastExtractionLazyQuery({
    fetchPolicy: 'no-cache',
  });

  const handleExtractionError = useCallback(async () => {
    const extractionData = await getExpertLastExtraction();
    if (extractionData.data?.expertLastExtraction.cvReupload) {
      displayToast(
        cvReuploadExtractionFailed(t, extractionData.data?.expertLastExtraction.createdAt),
        setExtractionAcknowledged,
      );
      return;
    }
    displayToast(extractionError(t), setExtractionAcknowledged);
  }, [displayToast]);

  const handleRealtimerHubStateUpdate = useCallback(async () => {
    const res = await getExpertHubState();
    const { expertHubState } = res.data ?? {};

    if (!expertHubState) {
      return;
    }
    const updatedExtractionState = expertHubState.extractionState as unknown as ExtractionStateEnum;

    if (updatedExtractionState === ExtractionStateEnum.Error) {
      await handleExtractionError();
    }
    loadHubStateData(expertHubState);
  }, [loadHubStateData, handleExtractionError]);

  useRealtimer(RealtimeAudienceEnum.HubStateUpdated, { expertId }, handleRealtimerHubStateUpdate);

  useEffect(() => {
    if (extractionState !== ExtractionStateEnum.Error || isExtractionAcknowledged) {
      return;
    }
    handleExtractionError();
  }, []);
};
